import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { fmtDate } from './TasksCommon';
import './Tasks.css';

const CONCL = {
  pending: { label: 'Ждём', color: '#f3f4f6', text: '#6b7280', icon: '⏳' },
  approved: { label: 'Согласовано', color: '#dcfce7', text: '#166534', icon: '✅' },
  approved_with_remarks: { label: 'С замечаниями', color: '#fef3c7', text: '#92400e', icon: '⚠️' },
  rejected: { label: 'Отклонено', color: '#fee2e2', text: '#b91c1c', icon: '❌' },
};

const STATUS_LABELS = {
  new: 'Новая',
  in_progress: 'В работе',
  review: 'На проверке',
  done: 'Готово',
  cancelled: 'Отменена',
};

export default function ProjectFlow({ projectId, onOpenTask }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getProjectMatrix(projectId);
      setData(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="tasks-empty">Загрузка…</div>;
  if (!data) return <div className="tasks-empty">Нет данных</div>;

  const total = data.rows.length;
  const doneCount = data.rows.filter((r) => r.conclusion_choice !== 'pending').length;
  const approved = data.rows.filter((r) => r.conclusion_choice === 'approved').length;

  let rows = data.rows;
  if (filter === 'pending') rows = rows.filter((r) => r.conclusion_choice === 'pending');
  if (filter === 'issues') rows = rows.filter((r) => r.conclusion_choice === 'rejected' || r.conclusion_choice === 'approved_with_remarks');
  if (filter === 'done') rows = rows.filter((r) => r.conclusion_choice === 'approved');

  return (
    <div>
      <div className="flow-summary">
        <div>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>Прогресс согласования</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#111' }}>
            {doneCount} из {total} <span style={{ fontSize: 15, color: '#6b7280' }}>({total ? Math.round((doneCount * 100) / total) : 0}%)</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 20 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#166534' }}>{approved}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>согласовано</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#92400e' }}>{data.rows.filter((r) => r.conclusion_choice === 'approved_with_remarks').length}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>с замечаниями</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#b91c1c' }}>{data.rows.filter((r) => r.conclusion_choice === 'rejected').length}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>отклонено</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#6b7280' }}>{total - doneCount}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>ждём</div>
          </div>
        </div>
      </div>

      <div className="flow-progress-bar">
        <div className="flow-progress-fill" style={{ width: `${total ? (doneCount * 100) / total : 0}%` }} />
      </div>

      <div className="tasks-toolbar" style={{ marginTop: 14 }}>
        {[
          { v: 'all', label: `Все (${total})` },
          { v: 'pending', label: `Ждём (${total - doneCount})` },
          { v: 'issues', label: `С замечаниями/отклонены (${data.rows.filter((r) => r.conclusion_choice === 'approved_with_remarks' || r.conclusion_choice === 'rejected').length})` },
          { v: 'done', label: `Согласованы (${approved})` },
        ].map((f) => (
          <button
            key={f.v}
            className={`tasks-btn ${filter === f.v ? 'primary' : ''}`}
            onClick={() => setFilter(f.v)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="tasks-empty">В этом фильтре пусто</div>
      ) : (
        <div className="flow-cards">
          {rows.map((r, i) => {
            const c = CONCL[r.conclusion_choice] || CONCL.pending;
            return (
              <div
                key={r.task_id}
                className="flow-card"
                style={{ background: c.color, borderColor: c.text, cursor: 'pointer' }}
                onClick={() => onOpenTask && onOpenTask(r.task_id)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ fontSize: 24 }}>{c.icon}</div>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: c.text,
                    padding: '3px 8px', borderRadius: 10, border: `1px solid ${c.text}`,
                    background: 'rgba(255,255,255,.5)',
                  }}>
                    {c.label}
                  </div>
                </div>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, color: '#111' }}>
                  {r.department}
                </div>
                <div style={{ fontSize: 12, color: '#4b5563' }}>
                  {r.assignee}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${c.text}` }}>
                  {STATUS_LABELS[r.status]} · до {fmtDate(r.due_date)}
                  {r.is_overdue && <span style={{ marginLeft: 6, color: '#b91c1c', fontWeight: 600 }}>ПРОСРОЧЕНО</span>}
                </div>
                {r.conclusion_text && (
                  <div style={{ fontSize: 12, color: '#111', marginTop: 6, fontStyle: 'italic', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    «{r.conclusion_text}»
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
